/**
 * QuestAutoOfferCard - Chain follow-up quest prompt
 *
 * Appears when a completed quest has a nextQuestId.
 * Shows the next quest details with Accept/Dismiss buttons.
 */

import { useMemo } from 'react';
import { QuestDefinition, QuestRewardType } from '@/types';

interface QuestAutoOfferCardProps {
  quest: QuestDefinition;
  completedQuestName: string;
  onAccept: () => void;
  onDismiss: () => void;
}

export function QuestAutoOfferCard({
  quest,
  completedQuestName,
  onAccept,
  onDismiss,
}: QuestAutoOfferCardProps) {
  const rewardSummary = useMemo(() => {
    return quest.rewards
      .map((r) => {
        switch (r.type) {
          case QuestRewardType.CURRENCY:
            return `+${r.value} gold`;
          case QuestRewardType.INVENTORY_EXPANSION:
            return `+${r.value} inventory`;
          case QuestRewardType.EXPERIENCE:
            return `+${r.value} XP`;
          default:
            return r.description || '';
        }
      })
      .filter(Boolean)
      .join(', ');
  }, [quest.rewards]);

  return (
    <div className="bg-gradient-to-b from-yellow-900/30 to-amber-900/20 border-2 border-yellow-500/60 rounded-lg p-4 transition-all">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">🎉</span>
        <div>
          <h4 className="text-sm font-bold text-yellow-300">New Quest Available!</h4>
          <p className="text-xs text-gray-400">
            Completed: {completedQuestName}
          </p>
        </div>
      </div>

      {/* Quest details */}
      <div className="bg-gray-800/60 rounded-lg p-3 mb-3">
        <h5 className="text-sm font-bold text-white mb-1">
          {quest.isTutorial && <span className="text-yellow-400">⭐ </span>}
          {quest.name}
        </h5>
        <p className="text-xs text-gray-400 mb-2">{quest.description}</p>
        <p className="text-xs text-yellow-400/80">Rewards: {rewardSummary}</p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          onClick={onAccept}
          className="flex-1 bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded-lg text-sm font-bold transition-colors cursor-pointer"
        >
          Accept Quest
        </button>
        <button
          onClick={onDismiss}
          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors cursor-pointer"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
